import os from "node:os";

import { ConnectionOfferV2Schema, type ConnectionOffer } from "../shared/connection-offer.js";

type BuildOfferEndpointsArgs = {
  listenHost: string;
  port: number;
};

export interface DirectEndpointCandidate {
  endpoint: string;
  source: string;
  observedAt: string;
}

export function buildOfferEndpoints({ listenHost, port }: BuildOfferEndpointsArgs): string[] {
  const endpoints: string[] = [];

  const isLoopbackHost = listenHost === "127.0.0.1" || listenHost === "localhost";
  const isWildcardHost = listenHost === "0.0.0.0" || listenHost === "::" || listenHost === "[::]";

  if (isWildcardHost) {
    const lanIp = getPrimaryLanIp();
    if (lanIp) {
      endpoints.push(`${lanIp}:${port}`);
    }
  } else if (!isLoopbackHost) {
    endpoints.push(`${listenHost}:${port}`);
  }

  endpoints.push(`localhost:${port}`);

  return dedupePreserveOrder(endpoints);
}

export function buildDirectEndpointCandidates({
  listenHost,
  port,
  observedAt = new Date().toISOString(),
}: BuildOfferEndpointsArgs & { observedAt?: string }): DirectEndpointCandidate[] {
  const endpoints: Array<{ endpoint: string; source: string }> = [];
  const isLoopbackHost = listenHost === "127.0.0.1" || listenHost === "localhost";
  const isWildcardHost = listenHost === "0.0.0.0" || listenHost === "::" || listenHost === "[::]";

  // Daemon-WS direct candidates: only when the daemon actually listens off-loopback.
  if (isWildcardHost) {
    const nets = os.networkInterfaces();
    for (const name of Object.keys(nets).sort()) {
      for (const addr of nets[name] ?? []) {
        if (!isUsefulLanInterfaceAddress(name, addr)) continue;
        if (addr.family === "IPv4") {
          endpoints.push({ endpoint: `${addr.address}:${port}`, source: `interface:${name}` });
        } else if (addr.family === "IPv6" && !addr.address.startsWith("fe80:")) {
          endpoints.push({ endpoint: `[${addr.address}]:${port}`, source: `interface:${name}` });
        }
      }
    }
  } else if (!isLoopbackHost) {
    endpoints.push({ endpoint: `${listenHost}:${port}`, source: "listen" });
  }

  // LAN SSH candidates: always enumerated, regardless of where the daemon
  // listens. Cross-host routing uses these to probe SSH at port 22 on the
  // sibling's LAN IPs even when the sibling's daemon HTTP listener is bound
  // to loopback (the installer default). The WS direct dialer filters out
  // `lan-interface:*` candidates (see `freshDirectEndpoints` in relay-dialer)
  // so they never get probed as daemon WS endpoints.
  const sshSeen = new Set<string>();
  const nets = os.networkInterfaces();
  for (const name of Object.keys(nets).sort()) {
    for (const addr of nets[name] ?? []) {
      if (!isUsefulLanInterfaceAddress(name, addr)) continue;
      if (addr.family !== "IPv4") continue; // SSH probing IPv6 is rarely useful on LANs
      if (sshSeen.has(addr.address)) continue;
      sshSeen.add(addr.address);
      // Port is the daemon's port; the SSH probe ignores it and dials 22 on
      // the hostname. The string must still be a non-empty `host:port` so the
      // auth-server's `daemonDirectEndpointSchema` accepts it.
      endpoints.push({
        endpoint: `${addr.address}:${port}`,
        source: `lan-interface:${name}`,
      });
    }
  }

  const seen = new Set<string>();
  return endpoints.flatMap((candidate) => {
    if (seen.has(candidate.endpoint)) return [];
    seen.add(candidate.endpoint);
    return [{ ...candidate, observedAt }];
  });
}

export async function createConnectionOfferV2(args: {
  serverId: string;
  daemonPublicKeyB64: string;
  relay: { endpoint: string };
}): Promise<ConnectionOffer> {
  return ConnectionOfferV2Schema.parse({
    v: 2,
    serverId: args.serverId,
    daemonPublicKeyB64: args.daemonPublicKeyB64,
    relay: args.relay,
  });
}

export function encodeOfferToFragmentUrl(args: {
  offer: ConnectionOffer;
  appBaseUrl: string;
}): string {
  const json = JSON.stringify(args.offer);
  const encoded = Buffer.from(json, "utf8").toString("base64url");
  return `${args.appBaseUrl.replace(/\/$/, "")}/#offer=${encoded}`;
}

function getPrimaryLanIp(): string | null {
  const override = process.env.APPOSTLE_PRIMARY_LAN_IP?.trim();
  if (override) return override;

  const nets = os.networkInterfaces();
  const names = Object.keys(nets).sort();

  for (const name of names) {
    const addrs = nets[name] ?? [];
    for (const addr of addrs) {
      if (addr.family === "IPv4" && !addr.internal) {
        return addr.address;
      }
    }
  }
  return null;
}

function isUsefulLanInterfaceAddress(name: string, addr: os.NetworkInterfaceInfo): boolean {
  if (addr.internal) return false;
  if (isVirtualInterfaceName(name)) return false;
  if (addr.family !== "IPv4" && addr.family !== "IPv6") return false;
  if (addr.family === "IPv4" && !isUsefulLanIpv4(addr.address)) return false;
  return true;
}

function isVirtualInterfaceName(name: string): boolean {
  return /^(br-|docker|veth|virbr|vmnet|utun|tailscale|tun|tap|zt|wg|lo)/i.test(name);
}

function isUsefulLanIpv4(address: string): boolean {
  const parts = address.split(".").map((part) => Number.parseInt(part, 10));
  if (
    parts.length !== 4 ||
    parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)
  ) {
    return false;
  }
  const [a, b] = parts;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  return false;
}

function dedupePreserveOrder(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}
